/**
 * We'll load the axios HTTP library which allows us to easily issue requests
 * to our Laravel back-end. This library automatically handles sending the
 * CSRF token as a header based on the value of the "XSRF" token cookie.
 */

import axios from 'axios';
import { useAuthStore } from '@stores/AuthStore.js';
import { resetClientStores } from '@stores/resetClientStores.js';

// A runtime override (window.__API_URL__) takes precedence over the build-time
// VITE_API_URL, letting Dusk point the SPA at the in-app mock without a rebuild.
axios.defaults.baseURL = (typeof window !== 'undefined' && window.__API_URL__ !== undefined)
  ? window.__API_URL__
  : import.meta.env.VITE_API_URL;
window.axios = axios;

window.axios.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest';

/**
 * If an authenticated request (one that carried our Bearer token) is rejected
 * with 401/403, the stored token was revoked or expired server-side. Perform a
 * full client-side logout — clear the session AND reset the draft stores — so
 * the app never stays stuck "signed in" with a dead token nor leaks the
 * previous user's data into the forms.
 */
window.axios.interceptors.response.use(
    (response) => response,
    (error) => {
        const status = error?.response?.status;
        const headers = error?.config?.headers ?? {};
        const sentBearer = String(headers.Authorization ?? headers.authorization ?? '').startsWith('Bearer ');

        if (sentBearer && [401, 403].includes(status)) {
            const authStore = useAuthStore();

            if (authStore.token) {
                authStore.clearSession();
                resetClientStores();
            }
        }

        return Promise.reject(error);
    },
);

/**
 * Echo exposes an expressive API for subscribing to channels and listening
 * for events that are broadcast by Laravel. Echo and event broadcasting
 * allows your team to easily build robust real-time web applications.
 */

import Echo from 'laravel-echo';

import Pusher from 'pusher-js';
window.Pusher = Pusher;

window.Echo = new Echo({
    broadcaster: 'reverb',
    key: import.meta.env.VITE_REVERB_APP_KEY,
    wsHost: import.meta.env.VITE_REVERB_HOST,
    wsPort: import.meta.env.VITE_REVERB_PORT,
    wssPort: import.meta.env.VITE_REVERB_PORT,
    forceTLS: (import.meta.env.VITE_REVERB_SCHEME ?? 'https') === 'https',
    enabledTransports: ['ws', 'wss'],
});
